import type { Variety } from '@protector/entities/crop/models';
import type { CurrentSeasonArea } from '@protector/entities/property/models';
import { GetAreaVariables, LoadRegionSeasonFieldsSuccess } from '@protector/entities/region/__legacy__/redux.actions';
import type { Region, RegionGeometry } from '@protector/entities/region/models';
import type { ScoutingScorePerField } from '@protector/entities/scouting-score/models';
import { getPropertyLatestsScoutingScores } from '@protector/entities/scouting-score/services';
import type { SeasonField } from '@protector/entities/season-field/models';
import type { Dictionary, UUID, V4Page } from '@protector/utils/types';
import * as MapFunctions from 'core/shared/map/map.functions';
import dayjs from 'dayjs';
import { getCropVarieties } from 'entities/crop/crop.service';
import { captureError } from 'infra/monitoring/monitoring.utils';
import { buildFieldScoutingScoreMap } from 'querys/scouting-score/scouting-score.functions';
import type { Observable } from 'rxjs';
import { from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { SeasonFieldsSuccessProps } from './region.epics.models';
import { buildAreaInfoBySeasonField, castToFourDecimalPlaces, fillAreaValues } from './region.utils';

export type ScoutingScoresForDetailedInfo =
  | { kind: 'empty'; latestScoutingScoresPerField: undefined; applyScoutingScores: undefined }
  | { kind: 'with-scores'; latestScoutingScoresPerField: ScoutingScorePerField; applyScoutingScores: (rootRegion: Region) => Region };

const emptyScoutingScores: ScoutingScoresForDetailedInfo = {
  kind: 'empty',
  latestScoutingScoresPerField: undefined,
  applyScoutingScores: undefined
};

interface FetchScoutingScoresArgs {
  propertyId: UUID;
  seasonIds: UUID[];
  currentAreaSeasons: CurrentSeasonArea[];
  systemFlags: Dictionary<boolean | string> | null;
  showScoutingScorePermission: boolean | undefined;
}

export const fetchLatestScoutingScoresForDetailedInfo$ = ({
  propertyId,
  seasonIds,
  currentAreaSeasons,
  systemFlags,
  showScoutingScorePermission
}: FetchScoutingScoresArgs): Observable<ScoutingScoresForDetailedInfo> => {
  const scoutingEnabledByPermission = !!systemFlags?.P40_21430_scoutingScoreXray || !!showScoutingScorePermission;
  if (!scoutingEnabledByPermission) {
    return of(emptyScoutingScores);
  }

  const scoutingSeasonId = seasonIds.length === 1 ? seasonIds[0] : undefined;

  return from(getPropertyLatestsScoutingScores(propertyId, scoutingSeasonId)).pipe(
    catchError(error => {
      captureError(error, { domain: 'Region' });
      return of(undefined);
    }),
    map(response => {
      const seasonAreaLookup = new Set(currentAreaSeasons.map(sa => `${sa.areaId}-${sa.seasonId}`));
      const filteredScoutingScores = response?.content?.filter(s => seasonAreaLookup.has(`${s.field_id}-${s.season_id}`));
      const latestScoutingScoresPerField =
        filteredScoutingScores && filteredScoutingScores.length > 0 ? buildFieldScoutingScoreMap(filteredScoutingScores) : undefined;

      if (!latestScoutingScoresPerField) return emptyScoutingScores;

      return {
        kind: 'with-scores' as const,
        latestScoutingScoresPerField,
        applyScoutingScores: (rootRegion: Region) => ({
          ...rootRegion,
          geometry: MapFunctions.addScoutingScoreToRegionProperties(rootRegion, latestScoutingScoresPerField) as RegionGeometry
        })
      };
    })
  );
};

const mapperCropVarieties = (cropIds: string[], varieties: V4Page<Variety>[]) => {
  const cropVarietiesMap: Record<string, Variety[]> = {};
  cropIds?.forEach((crop, idx) => {
    const filteredVarieties = varieties[idx].content;
    cropVarietiesMap[crop] = filteredVarieties;
  });

  return cropVarietiesMap;
};

export function getCropsByIds(cropIds: string[], companyId: string) {
  return cropIds.map(crop => getCropVarieties({ companyId: companyId, cropId: crop }));
}

export const loadRegionSeasonFieldsSuccess =
  ({ selectedSeasons, cropIds, regionChildrenMapping, region, areaSeasonFieldMap, seasons }: SeasonFieldsSuccessProps) =>
  (varieties: V4Page<Variety>[]) => {
    const mappedVarieties = mapperCropVarieties(cropIds, varieties);

    let areaInfo = buildAreaInfoBySeasonField({
      varieties: mappedVarieties,
      regionChildrenMapping,
      areaSeasonFieldMap,
      seasons,
      region
    });

    if (areaInfo) {
      areaInfo = fillAreaValues(areaInfo);
      areaInfo = castToFourDecimalPlaces(areaInfo);
    }

    return of(
      LoadRegionSeasonFieldsSuccess({
        seasonFieldsCropsVarieties: mappedVarieties,
        areaInfo: areaInfo ?? null
      }),
      GetAreaVariables(selectedSeasons)
    );
  };

const dayInUTC = (day?: string) => dayjs.utc(day);

const isInInclusiveRange = (target: dayjs.Dayjs, start: dayjs.Dayjs, end: dayjs.Dayjs): boolean =>
  !target.isBefore(start) && !target.isAfter(end);

export const isSetAreaSeasonField = (areaSeasonFieldMap: Record<string, SeasonField>, seasonField: SeasonField): boolean => {
  const seasonFieldInRecord = areaSeasonFieldMap[seasonField.field_id];
  if (!seasonFieldInRecord) return true;

  const seasonFieldInRecordStartDate = dayInUTC(seasonFieldInRecord?.start_date);
  const seasonFieldInRecordEndDate = dayInUTC(seasonFieldInRecord?.end_date);
  const today = dayInUTC();

  if (isInInclusiveRange(today, seasonFieldInRecordStartDate, seasonFieldInRecordEndDate)) return false;

  const seasonFieldStartDate = dayInUTC(seasonField?.start_date);
  const seasonFieldEndDate = dayInUTC(seasonField?.end_date);

  if (isInInclusiveRange(today, seasonFieldStartDate, seasonFieldEndDate)) return true;

  const isLaterSeasonField = seasonFieldInRecordStartDate.isBefore(seasonFieldStartDate);
  if (isLaterSeasonField) return true;

  return false;
};
